/* ==========================================================================
   Cart drawer v2
   Behaviour for sections/cart-drawer-v2.liquid.

   Nothing in assets/theme.js or assets/custom.js is modified. Instead the v2
   custom elements subclass the ones the theme already registered, which are
   retrieved with `customElements.get()`:

     <cart-drawer-v2>                  extends the theme's <cart-drawer>
     <free-shipping-bar-v2>            extends the theme's <free-shipping-bar>
     <cart-drawer-v2-recommendations>  collapses the cross-sell when it is empty

   The v2 markup keeps the theme's structural hooks (.line-item,
   .quantity-selector__*, data-line-id, input.freegiftId, .drawer__content,
   .drawer__footer) so the existing quantity / free-gift / shipping-insurance
   logic in custom.js keeps working untouched.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ATTRIBUTE = 'section';

  function whenDefined(name, callback) {
    if (window.customElements.get(name)) {
      callback(window.customElements.get(name));
      return;
    }

    window.customElements.whenDefined(name).then(function () {
      callback(window.customElements.get(name));
    });
  }

  /* ------------------------------------------------------------------------
     Stamped review badges USED TO BE REFRESHED HERE. Removed: the app is gone.

     snippets/loox-rating-badge.liquid replaced the empty
     <span class="stamped-product-reviews-badge"> these functions existed to fill,
     and layout/theme.liquid no longer loads Stamped's widget.min.js, so
     window.StampedFn is never defined. What was left ran on every cart change
     and every drawer open: a debounce, then twenty polls at 250ms apart before
     giving up - five seconds of timers per interaction, for an API that cannot
     arrive.
     ------------------------------------------------------------------------ */

  /* ------------------------------------------------------------------------
     Free shipping bar continuity across a re-render

     `_rerenderCart` replaces the drawer's innerHTML wholesale, so the bar the
     `cart:updated` handler just animated is thrown away and the server rendered
     one paints straight at its final width — the CSS transition never gets a
     previous value to run from. These two carry the width that was actually on
     screen at the moment of the swap onto the new bar, then hand it its target
     so the transition picks up where the old one left off.
     ------------------------------------------------------------------------ */

  /** The fraction of the track the fill currently covers, or null if no bar is up. */
  function readShippingProgress(drawer) {
    var track = drawer.querySelector('.cd2-progress__track');
    var fill = drawer.querySelector('.cd2-progress__fill');
    if (!track || !fill) return null;

    var trackWidth = track.getBoundingClientRect().width;
    /* A closed drawer measures 0 across; there is no visible width to preserve. */
    if (!trackWidth) return null;

    return fill.getBoundingClientRect().width / trackWidth;
  }

  function restoreShippingProgress(drawer, previousProgress) {
    var bar = drawer.querySelector('free-shipping-bar-v2');
    if (!bar) return;

    /* No bar before this render means the cart was empty, so the fill starts
       from nothing and grows — same thing the shopper would have seen had the
       bar been there all along. */
    var start = previousProgress === null ? 0 : previousProgress;
    var target = bar.style.getPropertyValue('--cd2-progress');
    if (!target) return;

    bar.style.setProperty('--cd2-progress', String(start));
    /* Flush the start width so the browser has two values to interpolate
       between rather than collapsing both into one style recalculation. */
    void bar.offsetWidth;
    bar.style.setProperty('--cd2-progress', target);
  }

  /* ------------------------------------------------------------------------
     <cart-drawer-v2>
     ------------------------------------------------------------------------ */

  whenDefined('cart-drawer', function (CartDrawer) {
    if (window.customElements.get('cart-drawer-v2')) return;

    var CartDrawerV2 = class extends CartDrawer {
      /**
       * The theme's implementation prefers `event.detail.cart.sections['mini-cart']`,
       * which renders the *default* drawer section. v2 always re-renders itself
       * from its own section id so the default section is never used here.
       */
      async _rerenderCart(event) {
        var sectionId = this.getAttribute(SECTION_ATTRIBUTE);
        var cartContent = (event && event.detail && event.detail.cart) || null;

        /* Re-render from the page the shopper is ON, not from /cart.

           The section picks its upsell pool from the viewed product's trig:
           tags first and the cart's second. Rendering it against the cart URL
           puts `product` out of scope, so every refresh threw the page's own
           pool away and fell back to the cart's - which is why the right
           upsells appeared on load and were replaced seconds later by the same
           default set on every page.

           The cart URL stays as the fallback. A path the Section Rendering API
           will not render for - anything that redirects or 404s - comes back
           without the element, and the drawer would otherwise be left empty. */
        var fetchSection = async function (base) {
          var response = await fetch(base + '?section_id=' + encodeURIComponent(sectionId));
          if (!response.ok) return null;
          var fakeDiv = document.createElement('div');
          fakeDiv.innerHTML = await response.text();
          return fakeDiv.querySelector('cart-drawer-v2');
        };

        var cartUrl = window.themeVariables.routes.cartUrl;
        var replacement = null;

        try {
          replacement = await fetchSection(window.location.pathname);
        } catch (e) {
          replacement = null;
        }

        if (!replacement && window.location.pathname !== cartUrl) {
          replacement = await fetchSection(cartUrl);
        }

        if (!replacement) return;

        var delay = (event && event.detail && event.detail.replacementDelay) || this.nextReplacementDelay || 0;

        setTimeout(() => {
          var scroller = this.querySelector('.drawer__content');
          var previousPosition = scroller ? scroller.scrollTop : 0;
          var previousProgress = readShippingProgress(this);

          this.innerHTML = replacement.innerHTML;

          restoreShippingProgress(this, previousProgress);

          var newScroller = this.querySelector('.drawer__content');

          if (cartContent && cartContent.item_count === 0) {
            if (newScroller && typeof newScroller.animate === 'function') {
              newScroller.animate(
                { opacity: [0, 1], transform: ['translateY(40px)', 'translateY(0)'] },
                { duration: 450, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' }
              );
            }
          } else if (newScroller) {
            newScroller.scrollTop = previousPosition;
          }

          if (event && event.detail && event.detail.openMiniCart) {
            void this.clientWidth;
            this.open = true;
          }
        }, delay);

        this.nextReplacementDelay = 0;
      }
    };

    window.customElements.define('cart-drawer-v2', CartDrawerV2);
  });

  /* ------------------------------------------------------------------------
     <free-shipping-bar-v2>

     Only the variable name differs from the theme element, which writes
     `--progress`; the v2 bar reads `--cd2-progress` so theme.css cannot move it.

     This runs on `cart:updated`, which fires just before `cart:refresh` — the
     bar animates to its new width immediately and the section re-render that
     follows swaps in the matching threshold copy.
     ------------------------------------------------------------------------ */

  whenDefined('free-shipping-bar', function (FreeShippingBar) {
    if (window.customElements.get('free-shipping-bar-v2')) return;

    var FreeShippingBarV2 = class extends FreeShippingBar {
      _onCartUpdated(event) {
        if (!event.detail || !event.detail.cart || !Array.isArray(event.detail.cart.items)) return;

        var threshold = this.threshold;
        if (!threshold || !isFinite(threshold)) return;

        var totalPrice = event.detail.cart.items
          .filter(function (item) {
            return item.requires_shipping;
          })
          .reduce(function (sum, item) {
            return sum + item.final_line_price;
          }, 0);

        var progress = Math.min(totalPrice / threshold, 1);
        this.style.setProperty('--cd2-progress', String(progress));
      }
    };

    window.customElements.define('free-shipping-bar-v2', FreeShippingBarV2);
  });

  /* ------------------------------------------------------------------------
     <cart-drawer-v2-recommendations>

     The grid is rendered server side by the section, which already excludes
     products present in the cart (Figma annotation: "Do not show products that
     are already in the cart"). This element only collapses the region when the
     exclusion leaves nothing to show — the drawer re-renders the whole section
     on every cart change, so no client-side fetching is needed.
     ------------------------------------------------------------------------ */

  if (!window.customElements.get('cart-drawer-v2-recommendations')) {
    var CartDrawerV2Recommendations = class extends HTMLElement {
      connectedCallback() {
        /*
          `.cd2-rec-card`, not `.pc28`: the redesign (Figma 14548:11447) replaced
          the shared `28-product-card` with a drawer-owned card, so `.pc28` no
          longer renders here and testing for it would collapse this region on
          every cart.
        */
        this.hidden = this.querySelector('.cd2-rec-card') === null;

        /*
          The track hides its scrollbar and fades its foot instead, so the haze is
          the only thing saying the list carries on. It has to lift once there is
          nothing left to scroll to, or the final card keeps its fade and its Add
          To Cart is unreadable — which is the one card a visitor scrolled down to
          reach.

          No teardown needed: the drawer replaces its innerHTML on every cart
          change, so this element and its listener are discarded together and a
          fresh pair is upgraded in their place.
        */
        var track = this.querySelector('.cd2-recs__grid');
        if (!track) return;

        var syncEnd = function () {
          var room = track.scrollHeight - track.clientHeight;
          // 1px of slack: fractional layout heights stop scrollTop ever landing
          // exactly on `room`, and the fade would never lift.
          if (room <= 1 || track.scrollTop >= room - 1) {
            track.setAttribute('data-cd2-end', '');
          } else {
            track.removeAttribute('data-cd2-end');
          }
        };

        track.addEventListener('scroll', syncEnd, { passive: true });
        syncEnd();
        // Card media can still be settling when this runs; re-read once laid out.
        window.requestAnimationFrame(syncEnd);
      }
    };

    window.customElements.define('cart-drawer-v2-recommendations', CartDrawerV2Recommendations);
  }

  /* ------------------------------------------------------------------------
     Cross-sell card — quantity stepper + add to cart

     Previously this was left to the delegated
     `.purchase-details__buttons .product-form__add-button` handler in
     assets/custom.js. The redesign (Figma 14548:11447) gave the card its own
     quantity stepper, and that handler posts a hardcoded `quantity: 1` while
     ignoring the form, so it can no longer serve this card. The card carries
     v2-only classes instead, which also makes it impossible for both handlers
     to fire on one click and add the line twice.

     Delegated from `document` rather than bound per card: the drawer replaces
     its own innerHTML on every `cart:refresh`, so anything bound directly to a
     card would be discarded on the first add.
     ------------------------------------------------------------------------ */

  var REC_MIN_QTY = 1;
  var REC_MAX_QTY = 99;

  function recRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function recClamp(value) {
    if (isNaN(value) || value < REC_MIN_QTY) return REC_MIN_QTY;
    if (value > REC_MAX_QTY) return REC_MAX_QTY;
    return value;
  }

  function recReadQty(card) {
    var input = card.querySelector('.cd2-rec-card__qty');
    return recClamp(parseInt(input && input.value, 10));
  }

  function recWriteQty(card, value) {
    var input = card.querySelector('.cd2-rec-card__qty');
    if (input) {
      input.value = value;
    }

    var minus = card.querySelector('.cd2-rec-card__step--minus');
    if (minus) {
      minus.disabled = value <= REC_MIN_QTY;
    }
  }

  function recAddToCart(button, card) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) {
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');

    var item = { id: Number(variantId), quantity: recReadQty(card) };

    /*
      Add on the subscription plan when the card is advertising a subscription
      price. Without this the line is added at the full variant price while the
      card shows the discounted one — the card said $39.95 and the cart line
      said $59.95. The attribute is only present when the variant has an
      allocation, so one-off products still post a plain line.
    */
    var sellingPlanId = button.getAttribute('data-selling-plan-id');
    if (sellingPlanId) {
      item.selling_plan = Number(sellingPlanId);
    }

    fetch(recRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('add failed');
        }
        return response.json();
      })
      .then(function () {
        /*
          Same three steps the shared handler performs after a successful add, so
          the drawer, the insurance line and the open state stay consistent with
          every other add to cart path on the site.
        */
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );

        var drawer = document.getElementById('mini-cart');
        if (drawer && !drawer.open) {
          drawer.open = true;
        }

        if (typeof window.shippingInsuranceAddRemove === 'function') {
          window.shippingInsuranceAddRemove();
        }

        /*
          `cart:refresh` re-renders the drawer after a delay, which replaces this
          button outright. The spinner is held for that window rather than
          cleared immediately, so the label cannot flash back for a frame before
          the new markup lands — the same 500ms the shared handler in
          assets/custom.js waits. If the re-render somehow does not happen, this
          still restores the button rather than spinning forever.
        */
        setTimeout(function () {
          button.classList.remove('is-loading');
          button.disabled = false;
        }, 500);
      })
      .catch(function () {
        /*
          The drawer re-renders from the server on `cart:refresh`, so a failed add
          only needs the button returned to a usable state — there is no local
          state to roll back.
        */
        button.classList.remove('is-loading');
        button.disabled = false;
      });
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) {
      return;
    }

    var step = event.target.closest('[data-cd2-rec-step]');
    if (step) {
      var stepCard = step.closest('.cd2-rec-card');
      if (!stepCard) {
        return;
      }
      event.preventDefault();
      recWriteQty(
        stepCard,
        recClamp(recReadQty(stepCard) + parseInt(step.getAttribute('data-cd2-rec-step'), 10))
      );
      return;
    }

    var button = event.target.closest('[data-cd2-rec-add]');
    if (!button) {
      return;
    }

    var card = button.closest('.cd2-rec-card');
    if (!card) {
      return;
    }

    event.preventDefault();
    recAddToCart(button, card);
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('.cd2-rec-card__qty')) {
      return;
    }

    var card = input.closest('.cd2-rec-card');
    if (card) {
      recWriteQty(card, recReadQty(card));
    }
  });
})();
